import { apiDelete, apiGet, apiGetPaginated, apiPost, apiPut } from '@/services/api/baseService';
import type {
  CrmProject,
  DevProject,
  ProjectMilestone,
  ProjectTask,
  TaskDependency,
  TimeEntry,
  ProjectMember,
  ProjectDashboardStats,
  GanttData,
  ProjectSource,
  ProjectLeaderCandidate,
} from '@/types/projects';

function base(source: ProjectSource) {
  return source === 'crm' ? '/api/crm/projects' : '/api/admin/dev-projects';
}

export const projectApi = {
  async list(source: ProjectSource, params?: Record<string, string>) {
    return apiGetPaginated<CrmProject | DevProject>(base(source), { params });
  },

  getStats(source: ProjectSource): Promise<ProjectDashboardStats> {
    return apiGet<ProjectDashboardStats>(`${base(source)}/stats`);
  },

  get(source: ProjectSource, id: number) {
    return apiGet<CrmProject | DevProject>(`${base(source)}/${id}`);
  },

  create(source: ProjectSource, data: Record<string, unknown>) {
    return apiPost<CrmProject | DevProject>(base(source), data);
  },

  update(source: ProjectSource, id: number, data: Record<string, unknown>) {
    return apiPut<CrmProject | DevProject>(`${base(source)}/${id}`, data);
  },

  remove(source: ProjectSource, id: number) {
    return apiDelete(`${base(source)}/${id}`);
  },

  createFromQuote(quoteId: number) {
    return apiPost<CrmProject>(`/api/crm/projects/from-quote/${quoteId}`);
  },

  createFromProcurement(procurementId: number) {
    return apiPost<CrmProject>(`/api/crm/projects/from-procurement/${procurementId}`);
  },

  listMilestones(source: ProjectSource, projectId: number) {
    return apiGet<ProjectMilestone[]>(`${base(source)}/${projectId}/milestones`);
  },

  createMilestone(source: ProjectSource, projectId: number, data: Partial<ProjectMilestone>) {
    return apiPost<ProjectMilestone>(`${base(source)}/${projectId}/milestones`, data);
  },

  updateMilestone(source: ProjectSource, milestoneId: number, data: Partial<ProjectMilestone>) {
    return apiPut<ProjectMilestone>(`${base(source)}/milestones/${milestoneId}`, data);
  },

  deleteMilestone(source: ProjectSource, milestoneId: number) {
    return apiDelete(`${base(source)}/milestones/${milestoneId}`);
  },

  listTasks(source: ProjectSource, projectId: number) {
    return apiGet<ProjectTask[]>(`${base(source)}/${projectId}/tasks`);
  },

  createTask(source: ProjectSource, projectId: number, data: Partial<ProjectTask>) {
    return apiPost<ProjectTask>(`${base(source)}/${projectId}/tasks`, data);
  },

  updateTask(source: ProjectSource, taskId: number, data: Partial<ProjectTask>) {
    return apiPut<ProjectTask>(`${base(source)}/tasks/${taskId}`, data);
  },

  deleteTask(source: ProjectSource, taskId: number) {
    return apiDelete(`${base(source)}/tasks/${taskId}`);
  },

  listDependencies(source: ProjectSource, projectId: number) {
    return apiGet<TaskDependency[]>(`${base(source)}/${projectId}/dependencies`);
  },

  createDependency(source: ProjectSource, taskId: number, dependsOnTaskId: number, dependencyType?: string) {
    return apiPost<TaskDependency>(`${base(source)}/tasks/${taskId}/dependencies`, {
      depends_on_task_id: dependsOnTaskId,
      dependency_type: dependencyType,
    });
  },

  deleteDependency(source: ProjectSource, dependencyId: number) {
    return apiDelete(`${base(source)}/dependencies/${dependencyId}`);
  },

  listTimeEntries(source: ProjectSource, projectId: number) {
    return apiGet<TimeEntry[]>(`${base(source)}/${projectId}/time-entries`);
  },

  createTimeEntry(source: ProjectSource, projectId: number, data: Partial<TimeEntry>) {
    return apiPost<TimeEntry>(`${base(source)}/${projectId}/time-entries`, data);
  },

  deleteTimeEntry(source: ProjectSource, projectId: number, entryId: number) {
    return apiDelete(`${base(source)}/${projectId}/time-entries/${entryId}`);
  },

  exportTimeCsv(projectId: number) {
    window.open(`/api/crm/projects/${projectId}/time-entries/export`, '_blank');
  },

  listMembers(source: ProjectSource, projectId: number) {
    return apiGet<ProjectMember[]>(`${base(source)}/${projectId}/members`);
  },

  addMember(source: ProjectSource, projectId: number, userId: string, role?: string) {
    return apiPost<ProjectMember>(`${base(source)}/${projectId}/members`, { user_id: userId, role });
  },

  removeMember(source: ProjectSource, projectId: number, userId: string) {
    return apiDelete(`${base(source)}/${projectId}/members/${userId}`);
  },

  getGantt(source: ProjectSource, projectId: number) {
    return apiGet<GanttData>(`${base(source)}/${projectId}/gantt`);
  },

  listManagerCandidates(source: ProjectSource, customerId?: string) {
    if (source === 'crm') {
      return apiGet<ProjectLeaderCandidate[]>(`${base(source)}/manager-candidates`, {
        params: { customer_id: customerId || '' },
      });
    }
    return apiGet<ProjectLeaderCandidate[]>(`${base(source)}/owner-candidates`);
  },

  listPrimaryContactCandidates(customerId: string) {
    return apiGet<ProjectLeaderCandidate[]>('/api/crm/projects/primary-contact-candidates', {
      params: { customer_id: customerId },
    });
  },
};
